import { ClerkProvider, useAuth, useClerk } from '@clerk/clerk-react';
import { useEffect, type ReactNode } from 'react';
import { env } from '../lib/env';
import { setTokenGetter, setUnauthorizedHandler } from '../lib/api';

/**
 * Bridges Clerk's hook-based token access into the plain-module API client, and
 * wires the single 401 policy.
 *
 * A 401 has two causes here and both end the same way:
 *
 *  - the session was revoked because the same credential signed in on another
 *    device (single-active-session is enforced by a Clerk webhook), or
 *  - the device was deactivated, so the `active` claim is now false.
 *
 * Both sign out and return to the login screen. Note the second case can loop:
 * signing back in genuinely succeeds and then every request 401s again, because
 * Clerk's `banUser` is a paid feature and the `active` claim is the only lock
 * (G44). That loop is accepted rather than special-cased.
 */
function ApiTokenBridge({ children }: { children: ReactNode }) {
  const { getToken } = useAuth();
  const { signOut } = useClerk();

  useEffect(() => {
    setTokenGetter(() =>
      env.clerkJwtTemplate ? getToken({ template: env.clerkJwtTemplate }) : getToken(),
    );
  }, [getToken]);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      void signOut();
    });
  }, [signOut]);

  return <>{children}</>;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  return (
    <ClerkProvider publishableKey={env.clerkPublishableKey} afterSignOutUrl="/">
      <ApiTokenBridge>{children}</ApiTokenBridge>
    </ClerkProvider>
  );
}
